import { useCallback, useState, useMemo } from 'react';
import { Form, Row, Table, TableColumnsType } from 'antd';
import { AButton, AModal, ATitle } from '../../../../components';
import {
  IPaymentMethod,
  IPaymentItemInsert,
  IDentalTreatment,
} from '../../../../shared/interfaces';
import { currency } from '../../../../shared/utils/functions';
import InsertPayment from '../../../../components/InsertPayment';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { formatDate } from '../../../../shared/utils/format-date';
import { useToast } from '../../../../hooks/toast';
import { useNavigate } from 'react-router-dom';
import { postDentalTreatmentPayment } from '../../../../http';

interface IConfirmDentalTreatment {
  open: boolean;
  close: () => void;
  dentalTreatment: IDentalTreatment | null;
  paymentsMethods: IPaymentMethod[];
  setApproval: React.Dispatch<React.SetStateAction<boolean>> | null;
}

const ConfirmDentalTreatment: React.FC<IConfirmDentalTreatment> = ({
  open,
  close,
  dentalTreatment,
  paymentsMethods,
  setApproval,
}) => {
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [paymentsItemsInsert, setPaymentsItemsInsert] = useState<
    IPaymentItemInsert[]
  >([]);
  const [paymentItemToEdit, setPaymentItemToEdit] =
    useState<IPaymentItemInsert | null>(null);
  const [isInsertModalOpen, setInsertModalOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const remaining = useMemo(() => {
    const totalInserted = paymentsItemsInsert.reduce(
      (acc, current) => acc + current.value,
      0,
    );
    const totalTreatment = dentalTreatment?.total ?? 0;
    return totalTreatment - totalInserted;
  }, [paymentsItemsInsert, dentalTreatment]);

  const handleRemove = useCallback((item: IPaymentItemInsert) => {
    setPaymentsItemsInsert((currentItems) =>
      currentItems.filter((e) => e.id !== item.id),
    );
  }, []);

  const handleOpenModal = useCallback((item: IPaymentItemInsert | null) => {
    setPaymentItemToEdit(item);
    setInsertModalOpen(true);
  }, []);

  const handleApproval = useCallback(async () => {
    if (remaining !== 0) {
      addToast({
        type: 'error',
        title: 'Pagamento Inválido',
        description:
          'O valor total dos pagamentos deve ser igual ao valor do tratamento.',
      });
      return;
    }

    setIsSubmitting(true);
    const data = {
      dental_treatment_id: dentalTreatment?.id,
      items: paymentsItemsInsert,
    };

    try {
      await postDentalTreatmentPayment(data);
      setApproval?.(true);
      addToast({
        type: 'success',
        title: 'Aprovado',
        description: 'Aprovado com sucesso.',
      });
      setTimeout(() => navigate(-1), 1000);
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro na Aprovação',
        description:
          error?.response?.data?.error ||
          'Não foi possível aprovar o tratamento.',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    remaining,
    paymentsItemsInsert,
    dentalTreatment,
    addToast,
    navigate,
    setApproval,
  ]);

  const columns = useMemo(
    (): TableColumnsType<IPaymentItemInsert> => [
      {
        title: '#',
        width: 40,
        render: (_, __, index) => <>{index + 1}</>,
      },
      {
        title: 'Pagamento',
        render: (cell: IPaymentItemInsert) => {
          return (
            paymentsMethods.find((x) => x.id === cell.payment_id)
              ?.description || 'N/A'
          );
        },
        width: 180,
      },
      { title: 'Parcelas', dataIndex: 'installment', width: 90 },
      {
        title: 'Vencimento',
        dataIndex: 'due_date',
        width: 110,
        render: (value: string) => <>{formatDate(value)}</>,
      },
      {
        title: 'Valor',
        dataIndex: 'value',
        width: 130,
        render: (value: number) => <>{currency(value)}</>,
      },
      {
        title: 'Opções',
        width: 120,
        render: (cell: IPaymentItemInsert) => (
          <Row style={{ gap: '2px' }}>
            <AButton
              type="primary"
              danger
              onClick={() => handleRemove(cell)}
              icon={<DeleteOutlined />}
              title="Excluir"
            />
            <AButton
              type="primary"
              onClick={() => handleOpenModal(cell)}
              icon={<EditOutlined />}
              title="Editar"
            />
          </Row>
        ),
      },
    ],
    [paymentsMethods, handleRemove, handleOpenModal],
  );

  return (
    <>
      <AModal
        open={open}
        close={close}
        title="Aprovar Tratamento"
        okText={'Aprovar'}
        onOk={handleApproval}
        confirmLoading={isSubmitting}
        width={760}
      >
        <Row style={{ display: 'flex', justifyContent: 'center' }}>
          <ATitle level={3} color={remaining < 0 ? 'red' : 'green'}>
            Restante: {currency(remaining)}
          </ATitle>
        </Row>
        <Row style={{ display: 'flex', justifyContent: 'end' }}>
          <AButton type="primary" onClick={() => handleOpenModal(null)}>
            Inserir pagamento
          </AButton>
        </Row>
        <Form layout="vertical">
          <Table
            style={{ width: '100%', marginTop: 10 }}
            scroll={{ y: 350 }}
            columns={columns}
            dataSource={paymentsItemsInsert}
            pagination={false}
            bordered
          />
        </Form>
      </AModal>

      {isInsertModalOpen && (
        <InsertPayment
          open={isInsertModalOpen}
          close={() => setInsertModalOpen(false)}
          paymentItemInsert={paymentItemToEdit}
          setPaymentsItemsInsert={setPaymentsItemsInsert}
          paymentsMethods={paymentsMethods}
          remaining={remaining}
        />
      )}
    </>
  );
};

export default ConfirmDentalTreatment;
